import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Send, 
  Users, 
  Search, 
  Trash2, 
  Eye, 
  EyeOff,
  Filter,
  RefreshCw,
  Plus,
  User,
  Mail,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import messageService from '../services/messageService';

const MessageManagementPage = () => {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [searchQuery, setSearchQuery] = useState('');
  const [messageStats, setMessageStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    today: 0
  });
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // 공지사항 메시지 통계
  const [noticeStats, setNoticeStats] = useState({
    total: 0,
    unread: 0,
    read: 0,
    today: 0
  });

  // 체크박스 선택 상태
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // 리스너 관리
  const [unsubscribe, setUnsubscribe] = useState(null);

  // 새 메시지 전송 폼 상태
  const [newMessage, setNewMessage] = useState({
    recipientId: '',
    recipientName: '',
    content: '',
    type: 'admin_message'
  });

  // 메시지 전송 타입 상태
  const [messageType, setMessageType] = useState('single'); // 'single', 'multiple', 'all'
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showUserSelector, setShowUserSelector] = useState(false);
  
  // 사용자 검색 상태
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const showMessage = (msg, type = 'error') => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 5000);
  };

  // 메시지 목록 조회 (페이지네이션)
  const fetchMessages = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
        setLastDoc(null);
        setMessages([]);
      } else {
        setLoadingMore(true);
      }
      
      console.log('📄 메시지 페이지네이션 조회:', { page, reset, filter, searchQuery });
      
      const result = await messageService.getMessagesPaginated(
        pageSize,
        reset ? null : lastDoc,
        filter,
        searchQuery
      );
      
      if (reset) {
        setMessages(result.messages);
      } else {
        setMessages(prev => [...prev, ...result.messages]);
      }
      
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
      
      // 통계 계산 및 전체 개수 업데이트 (전체 메시지 조회)
      const statsUnsubscribe = await messageService.getAllMessages((messageList) => {
        calculateStats(messageList);
        // 전체 개인 메시지 개수 계산
        const personalMessages = messageList.filter(msg => {
          const isNoticeMessage = msg.type === 'notice_message' || msg.noticeId;
          return !isNoticeMessage;
        });
        setTotalMessages(personalMessages.length);
      });
      
      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
      
      return statsUnsubscribe;
    } catch (error) {
      console.error('메시지 조회 실패:', error);
      showMessage('Failed to fetch messages.', 'error');
      setLoading(false);
      setLoadingMore(false);
      return null;
    }
  };

  // 다음 페이지 로드
  const loadNextPage = async () => {
    if (!hasMore || loadingMore) return;
    
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchMessages(nextPage, false);
  };

  // 필터/검색 변경 시 첫 페이지로 리셋
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    fetchMessages(1, true);
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    // 디바운싱을 위해 useEffect에서 처리
  };

  // 사용자 목록 조회
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      console.log('🔍 사용자 목록 조회 시작...');
      const userList = await messageService.getUsers();
      console.log('✅ 사용자 목록 조회 완료:', userList.length, '명');
      setUsers(userList);
      setUsersLoading(false);
    } catch (error) {
      console.error('❌ 사용자 조회 실패:', error);
      showMessage('Failed to fetch users.', 'error');
      setUsersLoading(false);
    }
  };

  // 통계 계산 (개인 메시지와 공지사항 메시지 분리)
  const calculateStats = (messageList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // 개인 메시지 (공지사항 제외)
    const personalMessages = messageList.filter(msg => {
      const isNoticeMessage = msg.type === 'notice_message' || msg.noticeId;
      return !isNoticeMessage;
    });
    
    // 공지사항 메시지
    const noticeMessages = messageList.filter(msg => {
      const isNoticeMessage = msg.type === 'notice_message' || msg.noticeId;
      return isNoticeMessage;
    });
    
    // 개인 메시지 통계
    const personalStats = {
      total: personalMessages.length,
      unread: personalMessages.filter(msg => !msg.read).length,
      read: personalMessages.filter(msg => msg.read).length,
      today: personalMessages.filter(msg => {
        const msgDate = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
        return msgDate >= today;
      }).length
    };
    
    // 공지사항 메시지 통계
    const noticeStats = {
      total: noticeMessages.length,
      unread: noticeMessages.filter(msg => !msg.read).length,
      read: noticeMessages.filter(msg => msg.read).length,
      today: noticeMessages.filter(msg => {
        const msgDate = msg.timestamp?.toDate ? msg.timestamp.toDate() : new Date(msg.timestamp);
        return msgDate >= today;
      }).length
    };
    
    setMessageStats(personalStats);
    setNoticeStats(noticeStats);
  };

  // 메시지 삭제
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      console.log('🗑️ [ADMIN-PAGE] 개별 메시지 삭제 시작:', messageId);
      const result = await messageService.deleteMessage(messageId);
      console.log('✅ [ADMIN-PAGE] 삭제 결과:', result);
      
      showMessage('Message deleted successfully.', 'success');
      
      // 메시지 리스트 새로고침
      await fetchMessages();
    } catch (error) {
      console.error('❌ [ADMIN-PAGE] 메시지 삭제 실패:', error);
      showMessage('Failed to delete message.', 'error');
    }
  };

  // 체크박스 선택/해제
  const toggleMessageSelection = (messageId) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(messageId)) {
      newSelected.delete(messageId);
    } else {
      newSelected.add(messageId);
    }
    setSelectedMessages(newSelected);
    setSelectAll(newSelected.size === messages.length && messages.length > 0);
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(messages.map(msg => msg.id)));
    }
    setSelectAll(!selectAll);
  };

  // 선택된 메시지들 일괄 삭제
  const deleteSelectedMessages = async () => {
    if (selectedMessages.size === 0) {
      showMessage('Please select messages to delete.', 'warning');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedMessages.size} selected messages?`)) return;

    try {
      console.log('🗑️ [ADMIN-PAGE] 일괄 삭제 시작:', {
        selectedCount: selectedMessages.size,
        selectedIds: Array.from(selectedMessages)
      });
      
      const deletePromises = Array.from(selectedMessages).map(messageId => 
        messageService.deleteMessage(messageId)
      );
      
      const results = await Promise.all(deletePromises);
      console.log('✅ [ADMIN-PAGE] 일괄 삭제 결과:', results);
      
      showMessage(`${selectedMessages.size} messages deleted successfully.`, 'success');
      setSelectedMessages(new Set());
      setSelectAll(false);
      
      // 메시지 리스트 새로고침
      await fetchMessages();
    } catch (error) {
      console.error('❌ [ADMIN-PAGE] 메시지 일괄 삭제 실패:', error);
      showMessage('Failed to delete messages.', 'error');
    }
  };

  // 관리자 메시지 전송
  const sendAdminMessage = async () => {
    if (!newMessage.content.trim()) {
      showMessage('Please enter message content.', 'error');
      return;
    }

    try {
      let result;
      
      if (messageType === 'all') {
        // 전체 사용자에게 전송
        console.log('📢 전체 사용자 메시지 전송 시작');
        result = await messageService.sendToAllUsers(newMessage.content);
        showMessage(`Message sent to ${result.totalUsers} users successfully.`, 'success');
      } else if (messageType === 'multiple') {
        // 선택된 사용자들에게 전송
        if (selectedUsers.length === 0) {
          showMessage('Please select users to send message to.', 'error');
          return;
        }
        console.log('📤 다중 사용자 메시지 전송 시작:', selectedUsers.length);
        result = await messageService.sendBulkAdminMessage(selectedUsers, newMessage.content);
        showMessage(`Message sent to ${result.sentCount} users successfully.`, 'success');
      } else {
        // 단일 사용자에게 전송
        if (!newMessage.recipientId) {
          showMessage('Please select a recipient.', 'error');
          return;
        }
        console.log('📤 단일 사용자 메시지 전송 시작:', {
        recipientId: newMessage.recipientId,
        recipientName: newMessage.recipientName,
        content: newMessage.content
      });
        result = await messageService.sendAdminMessage(
        newMessage.recipientId,
        newMessage.recipientName,
        newMessage.content
      );
        showMessage('Admin message sent successfully.', 'success');
      }
      
      // 폼 초기화
      setNewMessage({ recipientId: '', recipientName: '', content: '', type: 'admin_message' });
      setSelectedUsers([]);
      setMessageType('single');
      setShowSendModal(false);
      setShowUserSelector(false);
      
      // 사용자 검색 상태 초기화
      setUserSearchQuery('');
      setSearchResults([]);
      setIsSearching(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
    } catch (error) {
      console.error('❌ 메시지 전송 실패:', error);
      showMessage('Failed to send message.', 'error');
    }
  };

  // 사용자별 메시지 조회
  const getUserMessages = (userId) => {
    return messages.filter(msg => 
      msg.senderId === userId || msg.recipientId === userId
    );
  };

  // 사용자 선택/해제
  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(selected => selected.email === user.email);
      if (isSelected) {
        return prev.filter(selected => selected.email !== user.email);
      } else {
        return [...prev, { 
          id: user.id, 
          name: user.displayName || user.name || user.email,
          email: user.email
        }];
      }
    });
  };

  // 전체 사용자 선택/해제
  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => ({ 
        id: user.id, 
        name: user.displayName || user.name || user.email,
        email: user.email
      })));
    }
  };

  // 사용자 선택 초기화
  const clearUserSelection = () => {
    setSelectedUsers([]);
  };

  // 사용자 검색 함수
  const handleUserSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await messageService.searchUsers(query, 20);
      setSearchResults(results);
    } catch (error) {
      console.error('사용자 검색 실패:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // 사용자 검색어 변경 핸들러 (디바운싱)
  const handleUserSearchChange = (value) => {
    setUserSearchQuery(value);
    
    // 기존 타이머 클리어
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // 새로운 타이머 설정 (500ms 후 검색)
    const newTimeout = setTimeout(() => {
      handleUserSearch(value);
    }, 500);
    
    setSearchTimeout(newTimeout);
  };

  // 사용자 메시지 삭제
  const deleteUserMessages = async (userId) => {
    if (!window.confirm('Are you sure you want to delete all messages for this user?')) return;
    
    try {
      const result = await messageService.deleteUserMessages(userId);
      showMessage(`${result.deletedCount} user messages deleted successfully.`, 'success');
    } catch (error) {
      console.error('사용자 메시지 삭제 실패:', error);
      showMessage('Failed to delete user messages.', 'error');
    }
  };

  // 필터링은 서버 사이드에서 처리되므로 클라이언트 필터링 제거

  // 초기 로딩
  useEffect(() => {
    let unsubscribe = null;
    
    const initializeData = async () => {
      unsubscribe = await fetchMessages(1, true);
      fetchUsers();
    };
    
    initializeData();
    
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // 필터/검색 변경 시 리로드
  useEffect(() => {
    // 초기 로딩 중이면 스킵
    if (loading) return;
    
    const timeoutId = setTimeout(() => {
      fetchMessages(1, true);
    }, 500); // 검색어 디바운싱
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchQuery]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('ko-KR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const closeSendModal = () => {
    setShowSendModal(false);
    setMessageType('single');
    setSelectedUsers([]);
    setShowUserSelector(false);
    setUserSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }
  };

  const STAT_ITEMS = [
    { key: 'total', label: 'Total', value: messageStats.total, Icon: MessageSquare, cls: 'bg-indigo-50 text-indigo-600' },
    { key: 'unread', label: 'Unread', value: messageStats.unread, Icon: EyeOff, cls: 'bg-red-50 text-red-600' },
    { key: 'read', label: 'Read', value: messageStats.read, Icon: Eye, cls: 'bg-emerald-50 text-emerald-600' },
    { key: 'today', label: 'Today', value: messageStats.today, Icon: Calendar, cls: 'bg-amber-50 text-amber-600' },
  ];

  const FILTERS = [
    { key: 'all', label: 'All', Icon: MessageSquare },
    { key: 'unread', label: 'Unread', Icon: EyeOff },
    { key: 'read', label: 'Read', Icon: Eye },
  ];

  const SEND_TYPES = [
    { key: 'single', label: 'Single User', Icon: User },
    { key: 'multiple', label: 'Multiple Users', Icon: Users },
    { key: 'all', label: 'All Users', Icon: Mail },
  ];

  const initialOf = (name) => (name ? String(name).charAt(0).toUpperCase() : '?');

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header + Stats */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
                <MessageSquare className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">Message Management</h1>
                <p className="text-[11px] leading-tight text-slate-500">User messages and admin communications</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {STAT_ITEMS.map(({ key, label, value, Icon, cls }) => (
                <div key={key} className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-md ${cls}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span>
                    <span className="block text-[10px] leading-tight text-slate-500">{label}</span>
                    <span className="block text-sm font-bold leading-tight text-slate-900 tabular-nums">{(value || 0).toLocaleString()}</span>
                  </span>
                </div>
              ))}
              <button
                onClick={() => setShowUserModal(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                <Users className="h-3.5 w-3.5" />
                User Messages
              </button>
              <button
                onClick={() => setShowSendModal(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800"
              >
                <Send className="h-3.5 w-3.5" />
                Send Admin Message
              </button>
            </div>
          </div>
        </div>

        {/* Message list */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              {FILTERS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => handleFilterChange(key)}
                  className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors ${
                    filter === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            <div className="relative min-w-[220px] max-w-md flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by content, sender, or recipient..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {selectedMessages.size > 0 && (
                <button
                  onClick={deleteSelectedMessages}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-red-600 px-3 text-xs font-semibold text-white hover:bg-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete Selected ({selectedMessages.size})
                </button>
              )}
              <button
                onClick={() => fetchMessages(1, true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                title="Refresh"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {messages.length > 0 && (
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 cursor-pointer accent-slate-900"
                />
                {selectedMessages.size > 0 ? `${selectedMessages.size} selected` : `Select all (${messages.length})`}
              </label>
              <span className="text-[11px] text-slate-400">
                Showing {messages.length} of {totalMessages}
              </span>
            </div>
          )}

          <div className="p-2">
            {messages.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
                  <MessageSquare className="h-5 w-5 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-700">No messages found</p>
                <p className="mb-3 text-xs text-slate-500">No personal messages match the current filter.</p>
                {(filter !== 'all' || searchQuery) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      handleFilterChange('all');
                    }}
                    className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
                  >
                    View all messages
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {messages.map((msg) => {
                    const selected = selectedMessages.has(msg.id);
                    return (
                      <div
                        key={msg.id}
                        className={`group flex items-start gap-3 rounded-lg border px-3 py-2 transition-colors ${
                          selected ? 'border-indigo-300 bg-indigo-50/60' : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleMessageSelection(msg.id)}
                          className="mt-1.5 h-4 w-4 shrink-0 cursor-pointer accent-slate-900"
                        />
                        <span
                          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${
                            msg.isAdminMessage ? 'bg-slate-900' : 'bg-indigo-500'
                          }`}
                        >
                          {initialOf(msg.senderName)}
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
                            {!msg.read && <span className="h-1.5 w-1.5 rounded-full bg-red-500" title="Unread" />}
                            <span className="max-w-[40%] truncate font-semibold text-slate-900">{msg.senderName || 'Unknown'}</span>
                            <span className="text-slate-300">→</span>
                            <span className="max-w-[40%] truncate font-medium text-slate-700">{msg.recipientName || msg.recipientId || 'Unknown'}</span>
                            {msg.isAdminMessage && (
                              <span className="rounded bg-slate-900 px-1.5 py-px text-[10px] font-semibold text-white">ADMIN</span>
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap break-words text-xs leading-relaxed text-slate-600">
                            {msg.content}
                          </p>
                          <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-400">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatTime(msg.timestamp)}
                            </span>
                            {msg.read ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600">
                                <CheckCircle className="h-3 w-3" /> Read
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-500">
                                <XCircle className="h-3 w-3" /> Unread
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => deleteMessage(msg.id)}
                          className="shrink-0 rounded p-1.5 text-slate-400 opacity-70 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                          title="Delete Message"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {messages.length > 0 && (
                  <div className="mt-2 flex items-center justify-center border-t border-slate-100 pt-2">
                    {hasMore ? (
                      <button
                        onClick={loadNextPage}
                        disabled={loadingMore}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${loadingMore ? 'animate-spin' : ''}`} />
                        {loadingMore ? 'Loading...' : 'Load more'}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400">No more messages</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 관리자 메시지 전송 모달 */}
        {showSendModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
                    <Send className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-semibold leading-tight text-slate-900">Send Admin Message</h3>
                    <p className="text-[11px] leading-tight text-slate-500">
                      {usersLoading ? 'Loading user list...' : `${users.length} users available`}
                    </p>
                  </div>
                </div>
                <button onClick={closeSendModal} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-3 overflow-y-auto px-4 py-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-700">Send To</label>
                  <div className="grid grid-cols-3 gap-2">
                    {SEND_TYPES.map(({ key, label, Icon }) => (
                      <button
                        key={key}
                        onClick={() => {
                          setMessageType(key);
                          setShowUserSelector(key === 'multiple');
                          if (key !== 'multiple') setSelectedUsers([]);
                          setUserSearchQuery('');
                          setSearchResults([]);
                        }}
                        className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                          messageType === key
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {(messageType === 'single' || messageType === 'multiple') && (
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-700">
                        {messageType === 'single' ? 'Search Recipient' : `Select Recipients (${selectedUsers.length})`}
                      </label>
                      {messageType === 'multiple' && selectedUsers.length > 0 && (
                        <button onClick={clearUserSelection} className="text-[11px] text-slate-500 hover:text-slate-800">
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => handleUserSearchChange(e.target.value)}
                        placeholder="Search by name or email..."
                        className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-8 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      />
                      {isSearching && (
                        <RefreshCw className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
                      )}
                    </div>

                    {userSearchQuery && (
                      <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-slate-200">
                        {searchResults.length === 0 && !isSearching ? (
                          <div className="p-3 text-center text-xs text-slate-500">검색 결과가 없습니다</div>
                        ) : (
                          searchResults.map((user) => {
                            const displayName = user.displayName || user.name || user.email;
                            const isSelected =
                              messageType === 'single'
                                ? newMessage.recipientId === user.email
                                : selectedUsers.some((selected) => selected.email === user.email);
                            return (
                              <div
                                key={user.id}
                                onClick={() => {
                                  if (messageType === 'single') {
                                    setNewMessage({ ...newMessage, recipientId: user.email, recipientName: displayName });
                                    setUserSearchQuery(displayName);
                                    setSearchResults([]);
                                  } else {
                                    toggleUserSelection(user);
                                  }
                                }}
                                className={`flex cursor-pointer items-center gap-2.5 border-b border-slate-100 px-3 py-2 last:border-b-0 ${
                                  isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50'
                                }`}
                              >
                                {messageType === 'multiple' && (
                                  <input type="checkbox" readOnly checked={isSelected} className="h-4 w-4 accent-slate-900" />
                                )}
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
                                  {initialOf(displayName)}
                                </span>
                                <div className="min-w-0">
                                  <div className="truncate text-[13px] font-medium text-slate-800">{displayName}</div>
                                  <div className="truncate text-[11px] text-slate-500">{user.email}</div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}

                    {messageType === 'single' && newMessage.recipientId && (
                      <div className="mt-1.5 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
                        <span className="text-xs font-medium text-emerald-800">To: {newMessage.recipientName}</span>
                        <button
                          onClick={() => {
                            setNewMessage({ ...newMessage, recipientId: '', recipientName: '' });
                            setUserSearchQuery('');
                          }}
                          className="text-[11px] text-emerald-700 hover:text-emerald-900"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    {messageType === 'multiple' && selectedUsers.length > 0 && (
                      <div className="mt-1.5 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                        {selectedUsers.map((user) => (
                          <span
                            key={user.id}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 py-0.5 pl-2 pr-1 text-[11px] text-emerald-800"
                            title={user.email}
                          >
                            {user.name || user.email}
                            <button
                              onClick={() => setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id))}
                              className="rounded-full p-0.5 hover:bg-emerald-100"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {messageType === 'all' && (
                  <div
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${
                      users.length === 0 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-blue-200 bg-blue-50 text-blue-800'
                    }`}
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {users.length === 0
                      ? 'No registered users available to send messages to.'
                      : `Message will be sent to all ${users.length} users.`}
                  </div>
                )}

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-700">Message Content</label>
                    <span className="text-[11px] text-slate-400">{newMessage.content.length} chars</span>
                  </div>
                  <textarea
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                    rows={4}
                    className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
                    placeholder="Enter your message content..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
                <button onClick={closeSendModal} className="h-9 rounded-lg bg-slate-100 px-4 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                  Cancel
                </button>
                <button
                  onClick={sendAdminMessage}
                  disabled={users.length === 0}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  <Send className="h-3.5 w-3.5" />
                  {messageType === 'all'
                    ? `Send to All (${users.length})`
                    : messageType === 'multiple'
                      ? `Send to Selected (${selectedUsers.length})`
                      : 'Send'}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* 사용자 관리 모달 */}
        {showUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.15 }}
              className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <h3 className="text-[15px] font-semibold text-slate-900">사용자별 메시지 관리</h3>
                  <p className="text-[11px] text-slate-500">{usersLoading ? '로딩 중...' : `총 ${users.length}명의 사용자`}</p>
                </div>
                <button onClick={() => setShowUserModal(false)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-1.5 overflow-y-auto p-3">
                {usersLoading ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    사용자 목록을 불러오는 중...
                  </div>
                ) : users.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-500">사용자가 없습니다.</div>
                ) : (
                  users.map((user) => {
                    const userMessages = getUserMessages(user.email || user.id);
                    const displayName = user.displayName || user.name || user.email;
                    return (
                      <div key={user.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                            {initialOf(displayName)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-medium text-slate-900">{displayName}</p>
                            <p className="truncate text-[11px] text-slate-500">{user.email || user.id}</p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">메시지 {userMessages.length}개</span>
                          {userMessages.length > 0 && (
                            <button
                              onClick={() => deleteUserMessages(user.email || user.id)}
                              className="rounded-md bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-100"
                            >
                              전체 삭제
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* 메시지 알림 */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`fixed right-4 top-4 z-[60] rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
              message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MessageManagementPage;
